import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { type TableColumn } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { paymentManagementService, accountManagementService, type CreatePaymentData } from '../../services/admin';
import { subscriptionService } from '../../services/subscription';
import type { SubscriptionPayment, SubscriptionPlan } from '../../types/subscription';
import type { PaymentListParams, AdminAccount } from '../../types/admin';

/**
 * Payment Management Page (Admin Only)
 *
 * Provides system administrators with tools to:
 * - View all subscription payments with pagination and filtering
 * - Filter by account, payment method, and date range
 * - View payment details
 * - Create manual payment records
 * - Delete payment records
 */
export const PaymentManagementPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination & Filter state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [methodFilter, setMethodFilter] = useState<string>('');
  const [availableMethods, setAvailableMethods] = useState<string[]>([]);

  // Create payment modal state
  const [createModal, setCreateModal] = useState(false);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Form data for creating payment
  const [createFormData, setCreateFormData] = useState<CreatePaymentData>({
    account_id: 0,
    subscription_plan_id: 0,
    amount: 0,
    payment_method: 'manual',
    paid_at: new Date().toISOString().split('T')[0],
    notes: '',
    metadata: {},
  });

  // Proof images state
  const [proofImages, setProofImages] = useState<string[]>([]);

  // View payment modal state
  const [viewModal, setViewModal] = useState(false);
  const [viewingPayment, setViewingPayment] = useState<SubscriptionPayment | null>(null);

  // Fetch payments
  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: PaymentListParams = {
        page: currentPage,
        per_page: 15,
        ...(searchQuery && { search: searchQuery }),
        ...(methodFilter && { payment_method: methodFilter }),
      };

      const response = await paymentManagementService.getAllPayments(params);
      setPayments(response.data);
      setTotalPages(response.last_page);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('payment_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch payment methods on mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const methods = await paymentManagementService.getPaymentMethods();
        setAvailableMethods(methods);
      } catch (err) {
        console.error('Failed to load payment methods:', err);
      }
    };
    fetchPaymentMethods();
  }, []);

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchPayments();
  }, [currentPage, searchQuery, methodFilter]);

  // Handle open create modal
  const handleOpenCreateModal = async () => {
    setCreateModal(true);

    // Reset form
    setCreateFormData({
      account_id: 0,
      subscription_plan_id: 0,
      amount: 0,
      payment_method: 'manual',
      paid_at: new Date().toISOString().split('T')[0],
      notes: '',
      metadata: {},
    });
    setProofImages([]);

    // Load accounts and plans
    setLoadingAccounts(true);
    setLoadingPlans(true);

    try {
      const [accountsRes, plansRes] = await Promise.all([
        accountManagementService.getAllAccounts({ per_page: 100 }),
        subscriptionService.getAvailablePlans(),
      ]);
      setAccounts(accountsRes.data);
      setSubscriptionPlans(plansRes.plans);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts and plans');
    } finally {
      setLoadingAccounts(false);
      setLoadingPlans(false);
    }
  };

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProofImages((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove proof image
  const removeProofImage = (index: number) => {
    setProofImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit create payment
  const handleSubmitCreate = async () => {
    try {
      if (!createFormData.account_id || !createFormData.subscription_plan_id) {
        setError('Please select an account and subscription plan');
        return;
      }

      // Add proof images to metadata
      const metadata = {
        ...createFormData.metadata,
        proof_images: proofImages,
      };

      const paymentData: CreatePaymentData = {
        ...createFormData,
        metadata,
      };

      await paymentManagementService.createPayment(paymentData);
      setSuccess('Payment record created successfully');
      setCreateModal(false);
      fetchPayments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create payment');
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  // Table columns
  const columns: TableColumn<SubscriptionPayment>[] = [
    {
      key: 'id',
      header: t('payment_management.table.id'),
    },
    {
      key: 'account',
      header: t('payment_management.table.account'),
      render: (value) => {
        const account = value as { id: number; name: string; owner: { email: string } };
        return (
          <a
            href={`/admin/accounts?account_id=${account.id}`}
            className="text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = `/admin/accounts?account_id=${account.id}`;
            }}
          >
            {account.owner.email}
          </a>
        );
      },
    },
    {
      key: 'amount',
      header: t('payment_management.table.amount'),
      render: (value, row) => {
        const amount = Number(value);
        const payment = row as SubscriptionPayment;
        if (amount === 0 || payment.payment_method === 'bonus') {
          return (
            <span className="inline-flex items-center gap-1">
              <Badge variant="success">Free</Badge>
            </span>
          );
        }
        return formatCurrency(amount, 'USD');
      },
    },
    {
      key: 'payment_method',
      header: t('payment_management.table.method'),
      render: (value) => {
        const methodLabels: Record<string, string> = {
          crypto: t('payment_management.methods.crypto'),
          bank_transfer: t('payment_management.methods.bank'),
          manual: t('payment_management.methods.manual'),
          bonus: 'Bonus',
          stripe: 'Stripe',
        };
        const variant = value === 'bonus' ? 'success' : 'info';
        return (
          <Badge variant={variant}>
            {methodLabels[value as string] || String(value).replace('_', ' ')}
          </Badge>
        );
      },
    },
    {
      key: 'notes',
      header: t('payment_management.table.notes'),
      render: (value) => {
        if (!value) return '-';
        const notes = String(value);
        return notes.length > 30 ? `${notes.substring(0, 30)}...` : notes;
      },
    },
    {
      key: 'paid_at',
      header: t('payment_management.table.paid_date'),
      render: (value) => formatDate(String(value)),
    },
    {
      key: 'status',
      header: t('payment_management.table.status'),
      render: (value) => {
        const statusVariant =
          value === 'completed' ? 'success' :
          value === 'pending' ? 'warning' :
          value === 'failed' ? 'error' : 'default';
        const statusKey = value as 'completed' | 'pending' | 'failed';
        const statusLabels = {
          completed: t('payment_management.status.completed'),
          pending: t('payment_management.status.pending'),
          failed: t('payment_management.status.failed'),
        };
        return (
          <Badge variant={statusVariant}>
            {statusLabels[statusKey] || String(value).toUpperCase()}
          </Badge>
        );
      },
    },
  ];

  // Handle view payment details
  const handleViewPayment = async (payment: SubscriptionPayment) => {
    try {
      setError(null);
      // Fetch full payment details including relations
      const fullPayment = await paymentManagementService.getPaymentById(payment.id);
      setViewingPayment(fullPayment);
      setViewModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment details');
    }
  };

  // Render actions for each payment row
  const renderActions = (payment: SubscriptionPayment) => {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={() => handleViewPayment(payment)}
      >
        View Details
      </Button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('payment_management.page_title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('payment_management.page_subtitle')}
          </p>
        </div>
        <Button
          onClick={handleOpenCreateModal}
          className="w-full sm:w-auto"
        >
          Create Payment
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search Filter */}
          <div>
            <label htmlFor="search-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('payment_management.search_label')}
            </label>
            <Input
              id="search-filter"
              type="text"
              placeholder="Search by account, email, or notes..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Payment Method Filter */}
          <div>
            <label htmlFor="method-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('payment_management.method_filter_label')}
            </label>
            <select
              id="method-filter"
              value={methodFilter}
              onChange={(e) => {
                setMethodFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            >
              <option value="">{t('payment_management.method_filter_all')}</option>
              {availableMethods.map((method) => (
                <option key={method} value={method}>
                  {method.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Payments Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            <ResponsiveTable
              columns={columns}
              data={payments}
              emptyMessage={t('payment_management.table.empty_message')}
              renderActions={renderActions}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create Payment Modal */}
      <Modal
        open={createModal}
        onClose={() => setCreateModal(false)}
        title="Create Payment Record"
      >
        <div className="space-y-4">
          {/* Account Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account *
            </label>
            {loadingAccounts ? (
              <div className="text-sm text-gray-500">Loading accounts...</div>
            ) : (
              <select
                value={createFormData.account_id}
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, account_id: Number(e.target.value) })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Select an account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.owner.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Subscription Plan Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan *
            </label>
            {loadingPlans ? (
              <div className="text-sm text-gray-500">Loading plans...</div>
            ) : (
              <select
                value={createFormData.subscription_plan_id}
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, subscription_plan_id: Number(e.target.value) })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Select a plan</option>
                {subscriptionPlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.price}/{plan.billing_period}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Amount and Payment Method */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (USD) *
              </label>
              <Input
                type="number"
                value={createFormData.amount}
                onChange={(e) =>
                  setCreateFormData({ ...createFormData, amount: Number(e.target.value) })
                }
                step="0.01"
                min="0"
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method *
              </label>
              <select
                value={createFormData.payment_method}
                onChange={(e) =>
                  setCreateFormData({
                    ...createFormData,
                    payment_method: e.target.value as CreatePaymentData['payment_method'],
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="manual">Manual</option>
                <option value="crypto">Crypto</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="bonus">Bonus / Free</option>
              </select>
            </div>
          </div>

          {/* Paid Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Date *
            </label>
            <Input
              type="date"
              value={createFormData.paid_at}
              onChange={(e) => setCreateFormData({ ...createFormData, paid_at: e.target.value })}
              required
            />
          </div>

          {/* Notes (Explanation) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes / Explanation
            </label>
            <textarea
              value={createFormData.notes}
              onChange={(e) => setCreateFormData({ ...createFormData, notes: e.target.value })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Add any notes or explanation about this payment..."
            />
          </div>

          {/* Proof Images Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proof Images (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="mt-1 text-xs text-gray-500">Upload proof images (max 5MB each)</p>

            {/* Display uploaded images */}
            {proofImages.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {proofImages.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image}
                      alt={`Proof ${index + 1}`}
                      className="w-full h-24 object-cover rounded-md border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeProofImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <Button variant="secondary" onClick={() => setCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitCreate}>Create Payment</Button>
          </div>
        </div>
      </Modal>

      {/* View Payment Details Modal */}
      {viewModal && viewingPayment && (
        <Modal
          open={true}
          onClose={() => {
            setViewModal(false);
            setViewingPayment(null);
          }}
          title="Payment Details"
          size="large"
        >
          <div className="space-y-4">
            {/* Payment Info Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Payment ID:</span>{' '}
                <span className="text-gray-900">#{viewingPayment.id}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Status:</span>{' '}
                <Badge variant={viewingPayment.status === 'completed' ? 'success' : viewingPayment.status === 'pending' ? 'warning' : 'error'}>
                  {viewingPayment.status.charAt(0).toUpperCase() + viewingPayment.status.slice(1)}
                </Badge>
              </div>
              <div>
                <span className="font-medium text-gray-700">Amount:</span>{' '}
                <span className="text-gray-900 font-semibold">{formatCurrency(viewingPayment.amount, 'USD')}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Payment Method:</span>{' '}
                <Badge variant="info">
                  {(viewingPayment.payment_method as string).replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </Badge>
              </div>
              <div>
                <span className="font-medium text-gray-700">Paid Date:</span>{' '}
                <span className="text-gray-900">{formatDate(viewingPayment.paid_at)}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Created:</span>{' '}
                <span className="text-gray-900">{formatDate(viewingPayment.created_at)}</span>
              </div>
            </div>

            {/* Account Info */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Account Information</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Account:</span>{' '}
                  <span className="text-gray-900">{viewingPayment.account?.name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Owner Email:</span>{' '}
                  <span className="text-gray-900">{viewingPayment.account?.owner?.email}</span>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-700">Subscription Plan:</span>{' '}
                  <span className="text-gray-900">{viewingPayment.subscription_plan?.name || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {viewingPayment.notes && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Notes</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{viewingPayment.notes}</p>
                </div>
              </div>
            )}

            {/* Proof Images */}
            {viewingPayment.metadata && (viewingPayment.metadata as any).proof_images && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Proof Images</h4>
                <div className="grid grid-cols-2 gap-4">
                  {((viewingPayment.metadata as any).proof_images as string[]).map((image, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <img
                        src={image}
                        alt={`Proof ${index + 1}`}
                        className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(image, '_blank')}
                      />
                      <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600">
                        Click to view full size
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Created By */}
            {viewingPayment.created_by_user && (
              <div className="border-t border-gray-200 pt-4">
                <div className="text-sm">
                  <span className="font-medium text-gray-700">Created By:</span>{' '}
                  <span className="text-gray-900">
                    {(viewingPayment.created_by_user as any).name} ({(viewingPayment.created_by_user as any).email})
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button
                variant="primary"
                onClick={() => {
                  setViewModal(false);
                  setViewingPayment(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

PaymentManagementPage.displayName = 'PaymentManagementPage';
